import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileText,
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';

type ColumnMapping = {
  transactionDate: string | null;
  valueDate: string | null;
  amount: string | null;
  description: string | null;
  reference: string | null;
  counterpartyName: string | null;
  counterpartyIBAN: string | null;
  transactionType: string | null;
};

type ParsedTransaction = {
  transactionDate: Date;
  valueDate?: Date;
  amount: number;
  transactionType: 'CREDIT' | 'DEBIT';
  description?: string;
  reference?: string;
  counterpartyName?: string;
  counterpartyIBAN?: string;
};

const FIELD_OPTIONS = [
  { value: '', label: 'skipColumn' },
  { value: 'transactionDate', label: 'transactionDate' },
  { value: 'valueDate', label: 'valueDate' },
  { value: 'amount', label: 'amount' },
  { value: 'description', label: 'description' },
  { value: 'reference', label: 'reference' },
  { value: 'counterpartyName', label: 'counterpartyName' },
  { value: 'counterpartyIBAN', label: 'counterpartyIBAN' },
  { value: 'transactionType', label: 'transactionType' },
];

export function BankImportPage() {
  const { t, i18n } = useTranslation('reconciliation');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  // State
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [rawData, setRawData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [hasHeader, setHasHeader] = useState(true);
  const [delimiter, setDelimiter] = useState(',');
  const [dateFormat, setDateFormat] = useState('auto');
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    transactionDate: null,
    valueDate: null,
    amount: null,
    description: null,
    reference: null,
    counterpartyName: null,
    counterpartyIBAN: null,
    transactionType: null,
  });
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview'>('upload');
  const [error, setError] = useState<string | null>(null);

  // Queries
  const { data: bankAccounts } = trpc.account.list.useQuery({
    type: 'ASSET',
    subType: 'BANK',
    isActive: true,
  });

  const utils = trpc.useUtils();

  // Import mutation
  const importMutation = trpc.reconciliation.importBankStatement.useMutation({
    onSuccess: (data) => {
      utils.reconciliation.getBankTransactions.invalidate();
      navigate('/travel/accounting/reconciliation', {
        state: { imported: data.imported },
      });
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  // File drop handler
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setFile(file);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCSV(text);
    };
    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    maxFiles: 1,
  });

  // Parse CSV
  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    const detectDelimiter = (line: string): string => {
      const delimiters = [',', ';', '\t', '|'];
      let maxCount = 0;
      let detected = ',';
      for (const d of delimiters) {
        const count = (line.match(new RegExp(`\\${d}`, 'g')) || []).length;
        if (count > maxCount) {
          maxCount = count;
          detected = d;
        }
      }
      return detected;
    };

    const firstLine = lines[0];
    if (!firstLine) return;

    const detectedDelimiter = detectDelimiter(firstLine);
    setDelimiter(detectedDelimiter);

    const parseRow = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === detectedDelimiter && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const rows = lines.map(parseRow);
    setRawData(rows);

    const firstRow = rows[0];
    if (firstRow) {
      setHeaders(firstRow);
      autoDetectColumns(firstRow);
      setStep('mapping');
    }
  };

  // Auto-detect column mapping
  const autoDetectColumns = (headerRow: string[]) => {
    const mapping: ColumnMapping = {
      transactionDate: null,
      valueDate: null,
      amount: null,
      description: null,
      reference: null,
      counterpartyName: null,
      counterpartyIBAN: null,
      transactionType: null,
    };

    const datePatterns = ['date', 'tarih', 'işlem tarihi', 'transaction date'];
    const valueDatePatterns = ['value', 'valör', 'value date'];
    const amountPatterns = ['amount', 'tutar', 'miktar', 'sum', 'total'];
    const descPatterns = ['description', 'açıklama', 'desc', 'details', 'narrative'];
    const refPatterns = ['reference', 'referans', 'ref', 'id'];
    const namePatterns = ['name', 'ad', 'counterparty', 'karşı taraf', 'recipient', 'sender'];
    const ibanPatterns = ['iban', 'account', 'hesap'];

    headerRow.forEach((header, index) => {
      const h = header.toLowerCase();

      if (!mapping.transactionDate && datePatterns.some((p) => h.includes(p))) {
        mapping.transactionDate = index.toString();
      }
      if (!mapping.valueDate && valueDatePatterns.some((p) => h.includes(p))) {
        mapping.valueDate = index.toString();
      }
      if (!mapping.amount && amountPatterns.some((p) => h.includes(p))) {
        mapping.amount = index.toString();
      }
      if (!mapping.description && descPatterns.some((p) => h.includes(p))) {
        mapping.description = index.toString();
      }
      if (!mapping.reference && refPatterns.some((p) => h.includes(p))) {
        mapping.reference = index.toString();
      }
      if (!mapping.counterpartyName && namePatterns.some((p) => h.includes(p))) {
        mapping.counterpartyName = index.toString();
      }
      if (!mapping.counterpartyIBAN && ibanPatterns.some((p) => h.includes(p))) {
        mapping.counterpartyIBAN = index.toString();
      }
    });

    setColumnMapping(mapping);
  };

  // Parse date with various formats
  const parseDate = (value: string): Date | null => {
    if (!value) return null;

    // Try ISO format first
    const isoDate = new Date(value);
    if (!isNaN(isoDate.getTime())) return isoDate;

    // Try common formats
    const formats = [
      /^(\d{2})\/(\d{2})\/(\d{4})$/, // DD/MM/YYYY or MM/DD/YYYY
      /^(\d{2})-(\d{2})-(\d{4})$/, // DD-MM-YYYY
      /^(\d{4})-(\d{2})-(\d{2})$/, // YYYY-MM-DD
      /^(\d{2})\.(\d{2})\.(\d{4})$/, // DD.MM.YYYY
    ];

    for (const format of formats) {
      const match = value.match(format);
      if (match && match[1] && match[2] && match[3]) {
        let day: number, month: number, year: number;

        if (format.source.startsWith('^(\\d{4})')) {
          // YYYY-MM-DD
          year = parseInt(match[1], 10);
          month = parseInt(match[2], 10) - 1;
          day = parseInt(match[3], 10);
        } else {
          // Assume DD/MM/YYYY for Turkish format
          day = parseInt(match[1], 10);
          month = parseInt(match[2], 10) - 1;
          year = parseInt(match[3], 10);
        }

        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) return date;
      }
    }

    return null;
  };

  // Parse amount
  const parseAmount = (value: string): { amount: number; type: 'CREDIT' | 'DEBIT' } | null => {
    if (!value) return null;

    // Remove currency symbols and spaces
    let cleaned = value.replace(/[₺$€£\s]/g, '');

    // Handle Turkish number format (1.234,56)
    if (cleaned.includes(',') && cleaned.includes('.')) {
      if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
    } else if (cleaned.includes(',') && !cleaned.includes('.')) {
      cleaned = cleaned.replace(',', '.');
    }

    const amount = parseFloat(cleaned);
    if (isNaN(amount)) return null;

    return {
      amount: Math.abs(amount),
      type: amount >= 0 ? 'CREDIT' : 'DEBIT',
    };
  };

  // Parse transactions from data
  const parsedTransactions = useMemo((): ParsedTransaction[] => {
    if (!columnMapping.transactionDate || !columnMapping.amount) return [];

    const dataRows = hasHeader ? rawData.slice(1) : rawData;
    const transactions: ParsedTransaction[] = [];

    for (const row of dataRows) {
      const dateCol = parseInt(columnMapping.transactionDate, 10);
      const amountCol = parseInt(columnMapping.amount, 10);

      const dateValue = row[dateCol];
      const amountValue = row[amountCol];
      if (!dateValue || !amountValue) continue;

      const date = parseDate(dateValue);
      const amountResult = parseAmount(amountValue);

      if (!date || !amountResult) continue;

      const transaction: ParsedTransaction = {
        transactionDate: date,
        amount: amountResult.amount,
        transactionType: amountResult.type,
      };

      if (columnMapping.valueDate) {
        const valueDateValue = row[parseInt(columnMapping.valueDate, 10)];
        if (valueDateValue) {
          const valueDate = parseDate(valueDateValue);
          if (valueDate) transaction.valueDate = valueDate;
        }
      }

      if (columnMapping.description) {
        transaction.description = row[parseInt(columnMapping.description, 10)];
      }

      if (columnMapping.reference) {
        transaction.reference = row[parseInt(columnMapping.reference, 10)];
      }

      if (columnMapping.counterpartyName) {
        transaction.counterpartyName = row[parseInt(columnMapping.counterpartyName, 10)];
      }

      if (columnMapping.counterpartyIBAN) {
        transaction.counterpartyIBAN = row[parseInt(columnMapping.counterpartyIBAN, 10)];
      }

      transactions.push(transaction);
    }

    return transactions;
  }, [rawData, columnMapping, hasHeader]);

  // Handle import
  const handleImport = () => {
    if (!selectedAccountId) {
      setError(t('import.noAccount'));
      return;
    }

    if (parsedTransactions.length === 0) {
      setError(t('import.noFile'));
      return;
    }

    importMutation.mutate({
      accountId: selectedAccountId,
      transactions: parsedTransactions,
    });
  };

  // Reset
  const handleReset = () => {
    setFile(null);
    setRawData([]);
    setHeaders([]);
    setColumnMapping({
      transactionDate: null,
      valueDate: null,
      amount: null,
      description: null,
      reference: null,
      counterpartyName: null,
      counterpartyIBAN: null,
      transactionType: null,
    });
    setStep('upload');
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/travel/accounting/reconciliation')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('import.title')}</h1>
          <p className="text-muted-foreground">{t('import.subtitle')}</p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-3 border border-red-200 bg-red-50 p-4 text-red-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p>{error}</p>
          <Button variant="ghost" size="icon" className="ml-auto h-8 w-8" onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Account Selection */}
      <div className="border border-border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">{t('import.selectAccount')}</h2>
        <select
          value={selectedAccountId}
          onChange={(e) => setSelectedAccountId(e.target.value)}
          className="w-full max-w-md h-8 border border-border bg-background px-4 py-2 focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
        >
          <option value="">{t('import.selectAccountPlaceholder')}</option>
          {bankAccounts?.accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.code} - {account.name} ({account.currency})
            </option>
          ))}
        </select>
      </div>

      {/* File Upload */}
      {step === 'upload' && (
        <div className="border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">{t('import.uploadFile')}</h2>
          <div
            {...getRootProps()}
            className={cn(
              'flex flex-col items-center justify-center border-2 border-dashed p-12 cursor-pointer transition-colors',
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-center text-muted-foreground">{t('import.dragDrop')}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t('import.supportedFormats')}</p>
          </div>
        </div>
      )}

      {/* Column Mapping */}
      {step === 'mapping' && (
        <div className="space-y-6">
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{t('import.columnMapping')}</h2>
              <Button variant="outline" size="sm" className="h-8" onClick={handleReset}>
                <X className="mr-2 h-4 w-4" />
                {tCommon('clear')}
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mb-6">{t('import.mapColumns')}</p>

            {/* File options */}
            <div className="grid gap-4 md:grid-cols-3 mb-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hasHeader}
                  onChange={(e) => setHasHeader(e.target.checked)}
                  className="border border-border"
                />
                <span className="text-sm">{t('import.hasHeader')}</span>
              </label>
              <div>
                <label className="text-sm font-medium">{t('import.delimiter')}</label>
                <select
                  value={delimiter}
                  onChange={(e) => setDelimiter(e.target.value)}
                  className="mt-1 w-full h-8 border border-border bg-background px-3 py-1.5 text-sm focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
                >
                  <option value=",">{t('import.delimiters.comma')}</option>
                  <option value=";">{t('import.delimiters.semicolon')}</option>
                  <option value="\t">{t('import.delimiters.tab')}</option>
                  <option value="|">{t('import.delimiters.pipe')}</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('import.dateFormat')}</label>
                <select
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  className="mt-1 w-full h-8 border border-border bg-background px-3 py-1.5 text-sm focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
                >
                  <option value="auto">{t('import.dateFormats.auto')}</option>
                  <option value="dmy">{t('import.dateFormats.dmy')}</option>
                  <option value="mdy">{t('import.dateFormats.mdy')}</option>
                  <option value="ymd">{t('import.dateFormats.ymd')}</option>
                </select>
              </div>
            </div>

            {/* Column mapping grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {FIELD_OPTIONS.filter((f) => f.value).map((field) => (
                <div key={field.value}>
                  <label className="text-sm font-medium">
                    {t(`import.${field.label}`)}
                    {(field.value === 'transactionDate' || field.value === 'amount') && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <select
                    value={columnMapping[field.value as keyof ColumnMapping] || ''}
                    onChange={(e) =>
                      setColumnMapping((prev) => ({
                        ...prev,
                        [field.value]: e.target.value || null,
                      }))
                    }
                    className="mt-1 w-full h-8 border border-border bg-background px-3 py-1.5 text-sm focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
                  >
                    <option value="">{t('import.skipColumn')}</option>
                    {headers.map((header, index) => (
                      <option key={index} value={index.toString()}>
                        {header || `Column ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {/* Validation status */}
            <div className="mt-6 flex items-center gap-2">
              {columnMapping.transactionDate && columnMapping.amount ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {t('import.rowsToImport', { count: parsedTransactions.length })}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-600">
                  <Info className="h-5 w-5" />
                  <span className="text-sm">
                    {t('import.transactionDate')} and {t('import.amount')} are required
                  </span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={handleReset}>
                {tCommon('cancel')}
              </Button>
              <Button
                className="h-8"
                onClick={() => setStep('preview')}
                disabled={!columnMapping.transactionDate || !columnMapping.amount}
              >
                {t('import.previewData')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {step === 'preview' && (
        <div className="space-y-6">
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{t('import.previewData')}</h2>
              <Button variant="outline" size="sm" className="h-8" onClick={() => setStep('mapping')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('import.columnMapping')}
              </Button>
            </div>

            <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span>
                {file?.name} - {t('import.rowsToImport', { count: parsedTransactions.length })}
              </span>
            </div>

            {/* Preview table */}
            <div className="border border-border overflow-hidden">
              <div className="max-h-[400px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">{t('import.transactionDate')}</th>
                      <th className="px-4 py-2 text-left">{t('import.description')}</th>
                      <th className="px-4 py-2 text-left">{t('import.counterpartyName')}</th>
                      <th className="px-4 py-2 text-right">{t('import.amount')}</th>
                      <th className="px-4 py-2 text-center">{t('import.transactionType')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {parsedTransactions.slice(0, 50).map((tx, index) => (
                      <tr key={index} className="hover:bg-muted/30">
                        <td className="px-4 py-2">
                          {tx.transactionDate.toLocaleDateString(dateLocale)}
                        </td>
                        <td className="px-4 py-2 max-w-[200px] truncate">
                          {tx.description || '-'}
                        </td>
                        <td className="px-4 py-2">{tx.counterpartyName || '-'}</td>
                        <td className="px-4 py-2 text-right font-mono">
                          {formatCurrency(tx.amount, 'TRY')}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 text-xs font-medium',
                              tx.transactionType === 'CREDIT'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            )}
                          >
                            {t(`import.${tx.transactionType.toLowerCase()}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedTransactions.length > 50 && (
                <div className="bg-muted/50 px-4 py-2 text-sm text-muted-foreground text-center">
                  Showing first 50 of {parsedTransactions.length} transactions
                </div>
              )}
            </div>

            {/* Import button */}
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={() => setStep('mapping')}>
                {tCommon('back')}
              </Button>
              <Button
                className="h-8"
                onClick={handleImport}
                disabled={importMutation.isPending || !selectedAccountId}
              >
                {importMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('import.importing')}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    {t('import.importButton')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
